// @ts-strict-ignore
/** @jsx jsx */
import { jsx } from 'theme-ui'
import React, { useState, useRef } from 'react'
import FormatCurrency from '../../utils/currency-formater'
import { CALCULATE_BUTTON, CURRENCY_SIMBOL, RECALCULATE } from '../../constants'
import { Button } from '@uswitch/ev-components/lib/components/elements/trustyle-button'
import { Input } from '@uswitch/spark'
import DropDown from '@uswitch/ev-components/lib/components/elements/drop-down'
import { useBreakpointIndex } from '@theme-ui/match-media'
import { graduationYearOptions, loanTypes, monthNames } from '../../utils/helper'

interface IData {
  salary: number | string | undefined
  remainingDebt: number | string | undefined
  loanType: string
  graduationYear: string
  totalYearsLeft: number
  totalMonthsLeft: number
  monthlyRepaymentAmount: number
  totalRepaymentAmount: number
  monthlyRepaymentAmountPlusInterest: number
  paybackDate: Date
  writtenOffDate: Date
  treshold: number
  interestRate: number
  advSalaryIncrease: number | string | undefined
  advInterestRate: number | string | undefined
}

const StudentLoanCalculator = () => {
  const outputMessageRef = useRef<HTMLDivElement | null>(null)

  const executeScroll = () => {
    outputMessageRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    })
    if (outputMessageRef?.current) {
      outputMessageRef.current.style.display = 'flex'
    }
  }

  const [data, setData] = useState<IData>({
    salary: FormatCurrency(10000),
    remainingDebt: FormatCurrency(10000),
    loanType: 'Type 1',
    graduationYear: '2014',
    totalYearsLeft: 0,
    totalMonthsLeft: 0,
    monthlyRepaymentAmount: 0,
    totalRepaymentAmount: 0,
    monthlyRepaymentAmountPlusInterest: 0,
    paybackDate: new Date(),
    writtenOffDate: new Date(),
    treshold: 0,
    interestRate: 0,
    advSalaryIncrease: 4.0,
    advInterestRate: 3.5,
  })

  const [showAdvancedOptions, setShowAdvancedOptions] = useState<Boolean>(false)

  const breakPointIndexDesktop = useBreakpointIndex({ defaultIndex: 0 })

  const returnData = (key: string, returnValue: string | number | Date) => {
    let _returnValue: string | number | Date | undefined = returnValue

    if (key === 'salary' || key === 'remainingDebt') {
      _returnValue = returnValue ? FormatCurrency(returnValue) : 0
    }
    setData((prevData) => ({
      ...prevData,
      [key]: _returnValue,
    }))

    if (key !== 'advSalaryIncrease' && key !== 'advInterestRate' && outputMessageRef?.current) {
      outputMessageRef.current.style.display = 'none'
    }
  }

  const getMonthDifference = (startDate: Date, endDate: Date) => {
    return (
      endDate.getMonth() -
      startDate.getMonth() +
      12 * (endDate.getFullYear() - startDate.getFullYear())
    )
  }
  const cleanAmount = (value: any) => {
    if (value && value !== undefined) return value.toString().replace(/[^0-9]/g, '')
  }

  const calculateLoan = (e: any) => {
    e.preventDefault()
    const salary = parseFloat(data.salary !== undefined ? cleanAmount(data.salary) : '')
    const remainingDebt = parseFloat(
      data.remainingDebt !== undefined ? cleanAmount(data.remainingDebt) : '',
    )

    // This is where the magic happens
    // IMPORTANT: The following values (paybackPercentage, planThreshold and interest)
    // change yearly for each loan type
    // Source of truth: https://www.gov.uk/repaying-your-student-loan/what-you-pay

    // Since user inputs annual salary, divide by 12 to get monthly salary
    const monthlyWage = salary / 12
    let paybackPercentage = 0.09
    let planThreshold = 20184
    let interest = 0.015

    // Based on loan type, define treshold above which
    // the user pays back a loan, interest, and paybackPercentage
    switch (data.loanType) {
      case 'Type 1':
        planThreshold = 20184
        interest = 0.015
        break
      case 'Type 2':
        planThreshold = 27295
        interest = 0.045
        break
      case 'Type 4':
        planThreshold = 25375
        interest = 0.015
        break
      case 'Postgraduate':
        planThreshold = 21000
        interest = 0.045
        paybackPercentage = 0.06
        break
    }

    const monthlyRemainingAmount = Math.round(Math.abs(monthlyWage - planThreshold / 12))
    const monthlyRepaymentAmount = Math.round(monthlyRemainingAmount * paybackPercentage)
    let totalRepaymentAmount = remainingDebt + remainingDebt * interest
    let totalMonthsLeft = totalRepaymentAmount / monthlyRepaymentAmount

    const endDate = new Date()
    endDate.setMonth(endDate.getMonth() + Math.ceil(totalMonthsLeft))

    const writtenOffDate = new Date()
    writtenOffDate.setFullYear(writtenOffDate.getFullYear() + 30)

    // Calculate difference between end date and written of date to get exact numbers, if end date is larger that 30 years
    if (endDate > writtenOffDate) {
      const monthsDiff = getMonthDifference(writtenOffDate, endDate)
      totalRepaymentAmount = totalRepaymentAmount - monthsDiff * monthlyRepaymentAmount
      totalMonthsLeft = totalMonthsLeft - monthsDiff
    }

    // Add values to state
    setData({
      ...data,
      treshold: planThreshold,
      salary: salary,
      totalYearsLeft: totalMonthsLeft / 12,
      totalMonthsLeft: Math.ceil(totalMonthsLeft),
      monthlyRepaymentAmount: monthlyRepaymentAmount,
      totalRepaymentAmount: totalRepaymentAmount,
      paybackDate: endDate,
      writtenOffDate: writtenOffDate,
      interestRate: interest,
    })

    executeScroll()
  }

  function renderTable() {
    return (
      <table sx={table} cellPadding='0' cellSpacing='0'>
        <tbody>
          <tr sx={tableRows}>
            <td sx={tableCells}>Monthly repayments</td>
            <td sx={{ ...tableCells, minWidth: '30px' }}>
              {CURRENCY_SIMBOL}
              {data.monthlyRepaymentAmount !== 0 ? FormatCurrency(data.monthlyRepaymentAmount) : 0}
            </td>
          </tr>
          <tr sx={tableRows}>
            <td sx={tableCells}>Interest</td>
            <td sx={{ ...tableCells, minWidth: '30px' }}>
              {data.interestRate !== 0 ? data.interestRate + '%' : 0}
            </td>
          </tr>
          <tr sx={tableRows}>
            <td sx={tableCells}>Months left to pay</td>
            <td sx={{ ...tableCells, minWidth: '30px' }}>{Math.ceil(data.totalMonthsLeft)}</td>
          </tr>
        </tbody>
        <tfoot sx={tableFooter}>
          <tr sx={tableRows}>
            <td sx={tableCells}>Total left</td>
            <td sx={{ ...tableCells, minWidth: '100px' }}>
              {CURRENCY_SIMBOL}
              {data.totalRepaymentAmount !== 0 ? FormatCurrency(data.totalRepaymentAmount) : 0}
            </td>
          </tr>
        </tfoot>
        <tbody></tbody>
      </table>
    )
  }

  const inputContainerStyles = {
    width: '100%',
    marginBottom: 24,
    paddingLeft: 16,
    paddingRight: 16,
    alignSelf: 'self-end',
    boxSizing: 'border-box' as const,
  }

  const formInputContainerStyles = {
    display: 'grid',
    gridAutoFlow: 'column',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    maxWidth: '1092px',
    margin: 'auto',
  }

  return (
    <div>
      <div
        sx={{
          padding: '40px 0',
        }}
      >
        <div
          sx={{
            width: '100%',
            margin: '0 auto',
          }}
        >
          <h1
            sx={{
              color: 'plum',
              fontSize: '24px',
              fontWeight: '600',
              lineGHeight: '29px',
              letterSpacing: '0em',
              textAlign: 'left',
              margin: '0 0 12px 0',
            }}
          >
            Student Loans Calculator
          </h1>
          <div
            sx={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: '27px',
              letterSpacing: '0px',
              textAlign: 'left',
              marginBottom: '12px',
            }}
          >
            Please note that this calculator does not constitute financial advice.
          </div>
          <div
            sx={{
              background: '#F2F2F3',
              borderRadius: '4px',
              padding: '32px 16px',
            }}
          >
            <div
              sx={{
                maxWidth: ['360px', '100%', '100%'],
                margin: '0 auto',
              }}
            >
              <form onSubmit={(e) => calculateLoan(e)}>
                <div sx={breakPointIndexDesktop ? formInputContainerStyles : {}}>
                  <div sx={inputContainerStyles}>
                    <label htmlFor={'salary'} sx={labelStyles}>
                      Annual salary before tax
                    </label>
                    <div sx={InputStylesBox}>
                      <span sx={{ paddingLeft: '20px' }}>£</span>
                      <Input
                        sx={InputStyles}
                        type='text'
                        min={1000}
                        max={10000}
                        value={String(FormatCurrency(data.salary))}
                        name='salary'
                        onChange={(e: any) => returnData('salary', e.target.value)}
                      />
                    </div>
                  </div>
                  <div sx={inputContainerStyles}>
                    <label htmlFor={'remainingDebt'} sx={labelStyles}>
                      Remaining student debt
                    </label>
                    <div sx={InputStylesBox}>
                      <span sx={{ paddingLeft: '20px' }}>£</span>
                      <Input
                        sx={InputStyles}
                        type='text'
                        min={1000}
                        max={10000}
                        value={String(data.remainingDebt)}
                        name='remainingDebt'
                        onChange={(e: any) => returnData('remainingDebt', e.target.value)}
                      />
                    </div>
                  </div>
                  <div sx={inputContainerStyles}>
                    <label htmlFor={'graduationYear'} sx={labelStyles}>
                      Graduation year
                    </label>
                    <DropDown
                      sx={InputStyles}
                      onBlur={() => {}}
                      name='graduationYear'
                      onChange={(e: any) => returnData('graduationYear', e)}
                      options={graduationYearOptions()}
                      value={data.graduationYear}
                    ></DropDown>
                  </div>
                  <div sx={inputContainerStyles}>
                    <label htmlFor={'loanType'} sx={labelStyles}>
                      Student loan plan
                    </label>
                    <DropDown
                      sx={InputStyles}
                      onBlur={() => {}}
                      name='loanType'
                      onChange={(e: any) => returnData('loanType', e)}
                      options={loanTypes}
                      value={data.loanType}
                    ></DropDown>
                  </div>
                </div>
                <div
                  sx={{
                    ...inputContainerStyles,
                    width: '100%',
                    marginTop: breakPointIndexDesktop ? '20px' : '30px',
                    textAlign: 'center',
                  }}
                >
                  <div
                    sx={{
                      display: 'none',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                      paddingBottom: [56, 56, 43],
                      textAlign: 'center',
                      boxSizing: 'border-box',
                      wordBreak: 'break-all',
                    }}
                    ref={outputMessageRef}
                  >
                    <div sx={breakPointIndexDesktop ? autoFlowColoumnsStyles : {}}>
                      <p
                        sx={{
                          marginTop: 0,
                          marginBottom: 5,
                          fontSize: breakPointIndexDesktop ? '36px' : '18px',
                        }}
                      >
                        What I pay back
                      </p>
                      <h2
                        sx={{
                          color: 'plum',
                          fontWeight: '600',
                          fontSize: '36px',
                          margin: '0 0 10px',
                        }}
                      >
                        {CURRENCY_SIMBOL}
                        {data.salary && data.salary > data.treshold
                          ? data.totalRepaymentAmount !== 0
                            ? FormatCurrency(data.totalRepaymentAmount) + '*'
                            : 0
                          : 0}
                      </h2>
                    </div>
                    {data.salary && data.salary > data.treshold ? (
                      <div>
                        <div sx={breakPointIndexDesktop ? autoFlowColoumnsStyles : {}}>
                          <p
                            sx={{
                              marginTop: breakPointIndexDesktop ? 0 : 20,
                              marginBottom: 5,
                              fontSize: breakPointIndexDesktop ? '36px' : '18px',
                            }}
                          >
                            {data.paybackDate > data.writtenOffDate
                              ? 'my loan will be written off in'
                              : 'I can stop paying'}
                          </p>
                          <h2
                            sx={{
                              color: 'plum',
                              fontWeight: '600',
                              fontSize: '36px',
                              margin: '0 0 10px',
                            }}
                          >
                            {data.paybackDate > data.writtenOffDate ? (
                              <span>
                                {monthNames[data.writtenOffDate.getMonth()]}{' '}
                                {data.writtenOffDate.getFullYear()}
                              </span>
                            ) : (
                              <span>
                                {monthNames[data.paybackDate.getMonth()]}{' '}
                                {data.paybackDate.getFullYear()}
                              </span>
                            )}
                          </h2>
                        </div>
                        <h2
                          sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '21px',
                            letterApacing: '0px',
                            color: '#24262A',
                          }}
                        >
                          <u>
                            <a
                              sx={{ border: 'none', color: '#24262A' }}
                              href='https://www.money.co.uk/loans/how-much-do-student-loans-cost'
                              target='__blank'
                            >
                              How was this calculated?
                            </a>
                          </u>
                        </h2>
                        <div
                          sx={{
                            width: '100%',
                          }}
                        >
                          {renderTable()}
                        </div>
                      </div>
                    ) : (
                      <p
                        sx={{
                          fontSize: 18,
                          marginTop: 0,
                          marginBottom: 0,
                          wordBreak: 'break-word',
                          textAlign: breakPointIndexDesktop ? 'center' : 'left',
                          width: breakPointIndexDesktop ? '75%' : '100%',
                        }}
                      >
                        If you earn less than{' '}
                        <span sx={{ color: 'plum' }}>£{FormatCurrency(data.treshold)}</span>, you
                        don't need to pay anything back on your loan, however interest will continue
                        to be added to the total amount owed.
                      </p>
                    )}
                    <div sx={{ marginTop: '20px' }}>
                      <Button
                        sx={{ ...advanceOptionsButton, borderBottom: '1px solid #24262A' }}
                        onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      >
                        Show advanced options
                      </Button>
                      {showAdvancedOptions && (
                        <React.Fragment>
                          <div sx={advancedOptionsInputContainer}>
                            <label
                              htmlFor={'advSalaryIncrease'}
                              sx={{ ...labelStyles, textAlign: 'left' }}
                            >
                              Salary increase
                            </label>
                            <div sx={InputStylesBox}>
                              <Input
                                sx={AdvInputStyles}
                                type='text'
                                min={1000}
                                max={10000}
                                value={String(data.advSalaryIncrease)}
                                name='advSalaryIncrease'
                                onChange={(e: any) =>
                                  returnData('advSalaryIncrease', e.target.value)
                                }
                              />
                              <span sx={{ paddingRight: '20px' }}>%</span>
                            </div>
                          </div>
                          <div sx={advancedOptionsInputContainer}>
                            <label
                              htmlFor={'advInterestRate'}
                              sx={{ ...labelStyles, textAlign: 'left' }}
                            >
                              Interest rate
                            </label>
                            <div sx={InputStylesBox}>
                              <Input
                                sx={AdvInputStyles}
                                type='text'
                                min={1000}
                                max={10000}
                                value={String(data.advInterestRate)}
                                name='advInterestRate'
                                onChange={(e: any) => returnData('advInterestRate', e.target.value)}
                              />
                              <span sx={{ paddingRight: '20px' }}>%</span>
                            </div>
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                  <Button
                    variant='primary'
                    type='submit'
                    disabled={data?.salary === 0}
                    onClick={(e: Event) => calculateLoan(e)}
                    res={outputMessageRef}
                  >
                    {outputMessageRef?.current?.style.display === 'flex'
                      ? RECALCULATE
                      : CALCULATE_BUTTON}
                  </Button>
                </div>
              </form>
              <p
                sx={{
                  maxWidth: 1072,
                  margin: '30px auto 20px auto',
                  padding: 16,
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#ffffff',
                  background: '#34454E',
                  lineHeight: '21px',
                  letterSpacing: '0.23px',
                  textAlign: 'left',
                }}
              >
                What you pay back each month is based on what you earn and nothing else. But what
                you earn in the future - as well as what interest is added to the balance of the
                loan - is almost impossible to predict. This means the above is only an estimate,
                based on the current state of play. <br />
                <br />
                If you want to see more detailed examples of how it's all worked out and how it will
                affect you, take a look at our &nbsp;
                <a
                  sx={{ color: '#ffffff', border: 'none', '&:hover': { color: '#ffffff' } }}
                  href='https://www.money.co.uk/loans/how-much-do-student-loans-cost'
                  target='__blank'
                >
                  <u>How Much do Student Loans Cost</u>
                </a>{' '}
                page.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
const table = {
  width: '100%',
  maxWidth: '528px',
  margin: '43px auto 0',
  border: 'none',
}

const tableRows = {
  border: 'none',
}

const tableCells = {
  border: 'none',
  borderBottom: '1px solid #ADAFB3',
  fontSize: '14px',
  padding: '10px 5px',
  textAlign: 'right' as const,
  ':first-of-type': {
    textAlign: 'left' as const,
  },
}

const tableFooter = {
  backgroundColor: 'plum',
  color: '#FFFFFF',
  border: 'none',
}

const labelStyles = {
  display: 'block',
  fontSize: '18px',
  fontWeight: 700,
  marginBottom: 8,
  lineHeight: '22px',
  letterSpacing: '0em',
}

const InputStylesBox = {
  display: 'grid',
  gridAutoFlow: 'column',
  alignItems: 'center',
  border: '1px solid #ADAFB3',
  borderRadius: '5px',
  backgroundColor: '#fff',
}

const InputStyles = {
  input: {
    border: 'none',
    backgroundColor: '#fff',
    paddingLeft: 0,
  },
}

const AdvInputStyles = {
  input: {
    border: 'none',
    backgroundColor: '#fff',
    paddingLeft: 20,
  },
}

const autoFlowColoumnsStyles = {
  display: 'grid',
  gridAutoFlow: 'column',
  gridGap: '10px',
}

const advanceOptionsButton = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '21px',
  border: 'none',
  background: 'none',
  width: 'fit-content',
  padding: 0,
  marginBottom: '20px',
  color: '#24262A',
}

const advancedOptionsInputContainer = {
  display: 'grid',
  gridAutoFlow: 'column',
  alignItems: 'center',
  gridTemplateColumns: '1fr 1fr',
  margin: '10px 0',
}

export default StudentLoanCalculator
